import type { MouseEvent as ReactMouseEvent } from "react";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Modal from "../../../components/ui/Modal";
import type { AppliedFilterItem, PriceDetailsItem } from "../types";
import { formatPrice } from "../utils";

type Props = {
  open: boolean;
  title: string;
  subtitle?: string;
  appliedFilters?: AppliedFilterItem[];
  data: PriceDetailsItem[];
  loading: boolean;
  error?: string | null;
  onClose: () => void;
};

type FlatRow = {
  id: string;
  hotelId: string;
  hotelName: string;
  roomKey: string;
  roomName: string;
  category: string;
  platformKey: string;
  platformLabel: string;
  price: number;
  isBase: boolean;
};

type DisplayRow = FlatRow & {
  showHotel: boolean;
  hotelRowSpan: number;
  showRoom: boolean;
  roomRowSpan: number;
};

const PLATFORM_LABELS: Record<string, string> = {
  yandex: "Яндекс",
  tinkoff: "Тинькофф",
  tbank: "Тинькофф",
  ostrovok: "Островок",
  sutochno: "Суточно",
  traveline: "Сайт отеля",
  travelline: "Сайт отеля",
  alean: "Alean",
  booking: "Booking",
};

const PLATFORM_ORDER: Record<string, number> = {
  yandex: 1,
  tinkoff: 2,
  tbank: 2,
  ostrovok: 3,
  sutochno: 4,
  traveline: 5,
  travelline: 5,
  alean: 6,
  booking: 7,
};

const normalizePlatform = (value: string) => {
  const normalized = value.trim().toLowerCase();
  if (normalized === "tbank") return "tinkoff";
  if (normalized === "travelline") return "traveline";
  return normalized;
};

const isMissingPrice = (value: number | string | null | undefined) =>
  value === null || value === undefined || value === 0 || value === "0" || value === "none";

const formatPlatformLabel = (value: string) =>
  PLATFORM_LABELS[value] ??
  value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatCategoryLabel = (value: string) =>
  value
    ? value
        .replace(/_/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
    : "Без категории";

const getPlatformOrder = (value: string) => PLATFORM_ORDER[value] ?? Number.MAX_SAFE_INTEGER;
const HIDDEN_PLATFORMS = new Set(["sutochno"]);

const MIN_COLUMN_WIDTH = 120;
const DEFAULT_COLUMN_WIDTHS = {
  hotel: 340,
  room: 230,
  platform: 200,
  price: 170,
};

const buildDisplayRows = (sourceRows: FlatRow[]): DisplayRow[] => {
  const rows = sourceRows.map((row) => ({
    ...row,
    showHotel: false,
    hotelRowSpan: 0,
    showRoom: false,
    roomRowSpan: 0,
  }));

  let start = 0;
  while (start < rows.length) {
    let end = start + 1;
    while (end < rows.length && rows[end].hotelName === rows[start].hotelName) {
      end += 1;
    }

    rows[start].showHotel = true;
    rows[start].hotelRowSpan = end - start;

    let roomStart = start;
    while (roomStart < end) {
      let roomEnd = roomStart + 1;
      while (roomEnd < end && rows[roomEnd].roomKey === rows[roomStart].roomKey) {
        roomEnd += 1;
      }
      rows[roomStart].showRoom = true;
      rows[roomStart].roomRowSpan = roomEnd - roomStart;
      roomStart = roomEnd;
    }

    start = end;
  }

  return rows;
};

export default function PriceDetailsModalTest({
  open,
  title,
  subtitle,
  appliedFilters = [],
  data,
  loading,
  error,
  onClose,
}: Props) {
  const [search, setSearch] = useState("");
  const [columnWidths, setColumnWidths] = useState(DEFAULT_COLUMN_WIDTHS);
  const resizeRef = useRef<{ key: keyof typeof DEFAULT_COLUMN_WIDTHS; startX: number; startWidth: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    setSearch("");
  }, [open]);

  const expandedRows = useMemo<FlatRow[]>(() => {
    const rows = data.flatMap((item) => {
      const normalizedPlatforms = new Map<string, number>();
      Object.entries(item.prices ?? {}).forEach(([platform, value]) => {
        if (isMissingPrice(value)) return;
        const parsed = Number(value);
        if (Number.isNaN(parsed)) return;
        const normalizedPlatform = normalizePlatform(platform);
        if (HIDDEN_PLATFORMS.has(normalizedPlatform)) return;
        const current = normalizedPlatforms.get(normalizedPlatform);
        if (current === undefined || parsed < current) {
          normalizedPlatforms.set(normalizedPlatform, parsed);
        }
      });

      return Array.from(normalizedPlatforms.entries())
        .sort(([left], [right]) => {
          const leftOrder = getPlatformOrder(left);
          const rightOrder = getPlatformOrder(right);
          if (leftOrder !== rightOrder) return leftOrder - rightOrder;
          return left.localeCompare(right, "ru");
        })
        .map(([platformKey, price]) => ({
          id: `${item.hotelId}-${item.roomKey}-${platformKey}`,
          hotelId: item.hotelId,
          hotelName: item.hotelName,
          roomKey: item.roomKey,
          roomName: item.roomName,
          category: item.category,
          platformKey,
          platformLabel: formatPlatformLabel(platformKey),
          price,
          isBase: Boolean(item.isBase),
        }));
    });

    return rows.sort((left, right) => {
      const categoryCompare = left.category.localeCompare(right.category, "ru");
      if (categoryCompare !== 0) return categoryCompare;
      if (Number(left.isBase) !== Number(right.isBase)) {
        return Number(right.isBase) - Number(left.isBase);
      }
      const hotelCompare = left.hotelName.localeCompare(right.hotelName, "ru");
      if (hotelCompare !== 0) return hotelCompare;
      const roomCompare = left.roomName.localeCompare(right.roomName, "ru");
      if (roomCompare !== 0) return roomCompare;
      const leftOrder = getPlatformOrder(left.platformKey);
      const rightOrder = getPlatformOrder(right.platformKey);
      if (leftOrder !== rightOrder) return leftOrder - rightOrder;
      return left.platformLabel.localeCompare(right.platformLabel, "ru");
    });
  }, [data]);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return expandedRows;
    return expandedRows.filter((row) =>
      [row.hotelName, row.roomName, row.platformLabel, formatCategoryLabel(row.category)].some((value) =>
        value.toLowerCase().includes(query),
      ),
    );
  }, [expandedRows, search]);

  const categoryGroups = useMemo(() => {
    const grouped = new Map<string, FlatRow[]>();
    filteredRows.forEach((row) => {
      const bucket = grouped.get(row.category) ?? [];
      bucket.push(row);
      grouped.set(row.category, bucket);
    });

    return Array.from(grouped.entries()).map(([category, rows]) => ({
      category,
      categoryLabel: formatCategoryLabel(category),
      rows: buildDisplayRows(rows),
    }));
  }, [filteredRows]);

  const minMax = useMemo(() => {
    const values = filteredRows.map((row) => row.price);
    if (!values.length) return { min: 0, max: 0 };
    return { min: Math.min(...values), max: Math.max(...values) };
  }, [filteredRows]);

  const getPriceCellStyle = (value: number) => {
    if (minMax.max <= minMax.min) {
      return { backgroundColor: "rgba(148, 163, 184, 0.18)" };
    }
    const ratio = (value - minMax.min) / (minMax.max - minMax.min);
    const hue = 95 - ratio * 75;
    return { backgroundColor: `hsla(${hue.toFixed(0)}, 56%, 70%, 0.55)` };
  };

  const hasData = data.length > 0;
  const shownHotels = useMemo(() => new Set(filteredRows.map((row) => row.hotelName)).size, [filteredRows]);

  const handleResizeMove = useCallback((event: MouseEvent) => {
    const state = resizeRef.current;
    if (!state) return;
    const nextWidth = Math.max(MIN_COLUMN_WIDTH, state.startWidth + event.clientX - state.startX);
    setColumnWidths((current) => ({
      ...current,
      [state.key]: nextWidth,
    }));
  }, []);

  const stopResize = useCallback(() => {
    resizeRef.current = null;
    window.removeEventListener("mousemove", handleResizeMove);
    window.removeEventListener("mouseup", stopResize);
  }, [handleResizeMove]);

  const startResize = (key: keyof typeof DEFAULT_COLUMN_WIDTHS, event: ReactMouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
    resizeRef.current = {
      key,
      startX: event.clientX,
      startWidth: columnWidths[key],
    };
    window.addEventListener("mousemove", handleResizeMove);
    window.addEventListener("mouseup", stopResize);
  };

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", handleResizeMove);
      window.removeEventListener("mouseup", stopResize);
    };
  }, [handleResizeMove, stopResize]);

  return (
    <Modal open={open} onClose={onClose} title={title} widthClass="max-w-[70vw]">
      <div className="flex flex-col gap-4">
        {subtitle && <p className="-mt-1 text-sm text-slate-500">{subtitle}</p>}

        {appliedFilters.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {appliedFilters.map((filter) => (
              <span
                key={`${filter.label}-${filter.value}`}
                className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600"
              >
                <span className="font-semibold text-slate-700">{filter.label}:</span>
                <span>{filter.value}</span>
              </span>
            ))}
          </div>
        )}

        {loading && (
          <div className="grid min-h-[320px] place-items-center rounded-3xl border border-slate-200 bg-slate-50/70">
            <div className="flex items-center gap-4 text-base font-semibold text-slate-600">
              <span className="h-10 w-10 animate-spin rounded-full border-[3px] border-indigo-200 border-t-indigo-600" />
              Загружаем детализацию...
            </div>
          </div>
        )}

        {!loading && error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        {!loading && !error && hasData && (
          <>
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Поиск: отель, номер, платформа"
                className="h-11 min-w-[260px] rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 outline-none transition focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100"
              />
              <div className="text-sm text-slate-500">
                Отелей: {shownHotels} | Строк: {filteredRows.length}
              </div>
            </div>

            <div className="max-h-[70vh] overflow-auto rounded-3xl border border-slate-200">
              <table className="min-w-full border-collapse text-sm">
                <colgroup>
                  <col style={{ width: columnWidths.hotel }} />
                  <col style={{ width: columnWidths.room }} />
                  <col style={{ width: columnWidths.platform }} />
                  <col style={{ width: columnWidths.price }} />
                </colgroup>
                <thead className="sticky top-0 z-20">
                  <tr className="bg-slate-50 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                    <th className="relative border-r border-slate-200 px-4 py-3 text-left">
                      Отель
                      <span
                        role="separator"
                        aria-orientation="vertical"
                        onMouseDown={(event) => startResize("hotel", event)}
                        className="absolute right-0 top-0 h-full w-3 cursor-col-resize touch-none"
                      >
                        <span className="absolute right-1 top-1/2 h-8 w-px -translate-y-1/2 bg-slate-300" />
                      </span>
                    </th>
                    <th className="relative border-r border-slate-200 px-4 py-3 text-left">
                      Номер
                      <span
                        role="separator"
                        aria-orientation="vertical"
                        onMouseDown={(event) => startResize("room", event)}
                        className="absolute right-0 top-0 h-full w-3 cursor-col-resize touch-none"
                      >
                        <span className="absolute right-1 top-1/2 h-8 w-px -translate-y-1/2 bg-slate-300" />
                      </span>
                    </th>
                    <th className="relative border-r border-slate-200 px-4 py-3 text-left">
                      Платформа
                      <span
                        role="separator"
                        aria-orientation="vertical"
                        onMouseDown={(event) => startResize("platform", event)}
                        className="absolute right-0 top-0 h-full w-3 cursor-col-resize touch-none"
                      >
                        <span className="absolute right-1 top-1/2 h-8 w-px -translate-y-1/2 bg-slate-300" />
                      </span>
                    </th>
                    <th className="relative px-4 py-3 text-right">
                      Цена
                      <span
                        role="separator"
                        aria-orientation="vertical"
                        onMouseDown={(event) => startResize("price", event)}
                        className="absolute right-0 top-0 h-full w-3 cursor-col-resize touch-none"
                      >
                        <span className="absolute right-1 top-1/2 h-8 w-px -translate-y-1/2 bg-slate-300" />
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {categoryGroups.length > 0 ? (
                    categoryGroups.map((group) => (
                      <Fragment key={`group-${group.category}`}>
                        <tr className="border-y border-slate-200 bg-slate-100/80">
                          <td colSpan={4} className="px-4 py-2">
                            <span className="inline-flex rounded-xl border border-slate-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600">
                              {group.categoryLabel}
                            </span>
                          </td>
                        </tr>
                        {group.rows.map((row) => (
                          <tr
                            key={`${group.category}-${row.id}`}
                            className={[
                              "border-b border-slate-100 last:border-b-0",
                              row.showRoom ? "border-t-2 border-t-slate-300" : "",
                              row.isBase ? "bg-amber-50/60" : "bg-white",
                            ].join(" ")}
                          >
                            {row.showHotel && (
                              <td
                                rowSpan={row.hotelRowSpan}
                                className={[
                                  "border-r border-slate-100 px-4 py-3 align-top font-semibold",
                                  row.isBase ? "text-amber-900" : "text-slate-700",
                                ].join(" ")}
                              >
                                {row.hotelName}
                              </td>
                            )}

                            {row.showRoom && (
                              <td
                                rowSpan={row.roomRowSpan}
                                className="border-r border-slate-100 bg-slate-50/60 px-4 py-3 align-top font-medium text-slate-700"
                              >
                                {row.roomName}
                              </td>
                            )}

                            <td className="border-r border-slate-100 px-4 py-3 text-slate-600">{row.platformLabel}</td>
                            <td className="px-4 py-3 text-right font-semibold text-slate-800" style={getPriceCellStyle(row.price)}>
                              {formatPrice(row.price)}
                            </td>
                          </tr>
                        ))}
                      </Fragment>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="px-4 py-12 text-center text-sm text-slate-500">
                        По вашему запросу ничего не найдено
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {!loading && !error && !hasData && (
          <div className="grid min-h-[220px] place-items-center rounded-3xl border border-dashed border-slate-200 bg-slate-50/70 text-sm text-slate-500">
            Нет данных для отображения
          </div>
        )}
      </div>
    </Modal>
  );
}
